import { Chessground } from '@lichess-org/chessground';
import type { Api } from '@lichess-org/chessground/api';
import type { Config } from '@lichess-org/chessground/config';
import type { DrawShape } from '@lichess-org/chessground/draw';
import type { Color, Dests, Key, Role } from '@lichess-org/chessground/types';
import {
  candidatesFor,
  resolveUci,
  uciMovesToDests,
  uciToKeyPair,
  type UciMove,
} from './uci';
import { brushName, chessgroundBrushes, readBoardTheme } from './theme';

/**
 * ChessgroundAdapter — the ONLY board renderer (specification §6.2). It wraps
 * @lichess-org/chessground and never renders a board itself, and never decides
 * move legality or canonical identity: the server supplies legal UCI moves and
 * this adapter only converts between UCI and Chessground key/destination form.
 *
 * UCI -> Chessground: each legal UCI move contributes an `orig -> dest` entry to
 * the `Dests` map (promotion moves collapse onto the one destination square).
 * Chessground -> UCI: the `after` callback maps the (orig, dest) pair back to
 * the legal UCI candidate(s); a single candidate is emitted verbatim, multiple
 * candidates (promotion) pause for an accessible modal and only then emit.
 */

export type MoveHandler = (uci: UciMove) => void;
export type PromotionHandler = (candidates: UciMove[]) => void;

/**
 * A product-owned arrow rendered by Chessground (review answers, ideas, etc.). The
 * brush is a role — green prepared/good, red played/bad, blue alternative, yellow
 * info — and the board skin decides its colour.
 */
export interface BoardArrow {
  uci: UciMove;
  brush: 'green' | 'red' | 'blue' | 'yellow';
  label?: string;
}

/** Respect the user's OS-level reduced-motion preference (WCAG 2.2 AA 2.3.3). */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface BoardInput {
  /** Canonical FEN from the API (`PositionNode.canonicalFen`). */
  fen: string;
  /** Side to move (`PositionNode.sideToMove`). */
  turnColor: 'w' | 'b';
  /** Board orientation; defaults to white at the bottom. */
  orientation?: 'w' | 'b';
  /** Legal UCI moves for the side to move (outgoing edge transitions). */
  dests: UciMove[];
  /** Last played UCI move to highlight, when known. */
  lastMove?: UciMove;
  /** Non-interactive arrows supplied by the review/analysis surface. */
  arrows?: BoardArrow[];
}

export interface ChessgroundAdapterOptions {
  onMove: MoveHandler;
  /** Called with the promotion candidates; the modal later calls `submitPromotion`. */
  onPromotion: PromotionHandler;
}

interface PendingPromotion {
  orig: Key;
  dest: Key;
  candidates: UciMove[];
}

export class ChessgroundAdapter {
  private api: Api | null = null;
  private legalMoves: UciMove[] = [];
  private dests: Dests = new Map();
  private turnColor: Color = 'white';
  private currentFen = '';
  private pendingPromotion: PendingPromotion | null = null;
  private movePending = false;

  constructor(private readonly options: ChessgroundAdapterOptions) {}

  /** Create the Chessground instance bound to an element, showing `initial`
   *  (or a neutral placeholder) immediately so no start-position flash occurs
   *  when mounting onto a mid-line position. */
  mount(element: HTMLElement, initial?: BoardInput): void {
    const input = initial ?? this.placeholderInput();
    this.legalMoves = [...input.dests];
    this.dests = uciMovesToDests(input.dests);
    this.turnColor = input.turnColor === 'b' ? 'black' : 'white';
    this.currentFen = input.fen;
    this.api = Chessground(element, this.fullConfig(input));
  }

  /** Reconfigure the board for a new position/legal-move set. */
  set(input: BoardInput): void {
    // React/query updates may still describe the source position while the
    // server processes a move. Keep Chessground's already-dropped piece there.
    if ((this.movePending || this.pendingPromotion) && input.fen === this.currentFen) {
      this.setOrientation(input.orientation ?? 'w');
      this.api?.setAutoShapes(this.autoShapes(input));
      return;
    }
    this.movePending = false;
    this.legalMoves = [...input.dests];
    this.dests = uciMovesToDests(input.dests);
    this.turnColor = input.turnColor === 'b' ? 'black' : 'white';
    this.currentFen = input.fen;
    this.pendingPromotion = null;
    this.api?.set(this.fullConfig(input));
  }

  setOrientation(orientation: 'w' | 'b'): void {
    this.api?.set({ orientation: orientation === 'b' ? 'black' : 'white' });
  }

  /** Highlight a square via the native selection highlight (keyboard focus). */
  selectSquare(key: Key | null): void {
    this.api?.selectSquare(key, true);
  }

  getSelectedSquare(): Key | null {
    return this.api?.state.selected ?? null;
  }

  /** Legal destination squares for a piece (for keyboard navigation). */
  destinationsFor(orig: Key): Key[] {
    return this.dests.get(orig) ?? [];
  }

  /** Draw a visible focus indicator on a square (keyboard cursor). */
  setFocus(key: Key | null): void {
    this.api?.set({
      highlight: { custom: key ? new Map([[key, 'chessreps-focus']]) : new Map() },
    });
  }

  /**
   * Convert a board (orig, dest) pair back to UCI and route it. This is the
   * single choke point used by drag/click (`after`) and keyboard input alike.
   * It emits UCI candidates only — legality was already established by the
   * server-provided dests.
   */
  attemptMove(orig: Key, dest: Key): void {
    if (this.movePending || this.pendingPromotion) return;
    const candidates = candidatesFor(this.legalMoves, orig, dest);
    if (candidates.length === 0) return;
    if (candidates.length > 1) {
      this.pendingPromotion = { orig, dest, candidates };
      this.options.onPromotion([...candidates]);
      return;
    }
    this.movePending = true;
    this.options.onMove(candidates[0]);
  }

  /** Resolve a pending promotion with the chosen piece (queen/rook/bishop/knight). */
  submitPromotion(role: Role): void {
    if (this.pendingPromotion === null || this.api === null) return;
    const { orig, dest, candidates } = this.pendingPromotion;
    this.pendingPromotion = null;
    const uci = resolveUci(candidates, orig, dest, role);
    if (uci === null) {
      this.resync();
      return;
    }
    // Chessground has no promotion logic; the pawn is already on `dest`. Replace
    // it with the chosen piece, then emit the UCI.
    this.api.setPieces(new Map([[dest, { role, color: this.turnColor }]]));
    this.movePending = true;
    this.options.onMove(uci);
  }

  /** Restore the authoritative position when the parent declines a move. */
  cancelMove(): void {
    this.movePending = false;
    this.resync();
  }

  cancelPromotion(): void {
    if (this.pendingPromotion === null || this.api === null) return;
    this.pendingPromotion = null;
    this.resync();
  }

  destroy(): void {
    this.api?.destroy();
    this.api = null;
  }

  private resync(): void {
    if (this.api === null) return;
    this.api.set({
      fen: this.currentFen,
      turnColor: this.turnColor,
      movable: { free: false, color: this.turnColor, dests: this.dests },
    });
  }

  private readonly handleAfter = (orig: Key, dest: Key): void => {
    this.attemptMove(orig, dest);
  };

  private placeholderInput(): BoardInput {
    // Chessground's own initial FEN + white to move; replaced on first `set()`.
    return {
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      turnColor: 'w',
      dests: [],
    };
  }

  private fullConfig(input: BoardInput): Config {
    const color: Color = input.turnColor === 'b' ? 'black' : 'white';
    const reduceMotion = prefersReducedMotion();
    return {
      fen: input.fen,
      turnColor: color,
      orientation: input.orientation === 'b' ? 'black' : 'white',
      coordinates: true,
      animation: { enabled: !reduceMotion, duration: reduceMotion ? 0 : 200 },
      lastMove: input.lastMove ? (uciToKeyPair(input.lastMove) ?? undefined) : undefined,
      highlight: { lastMove: true, check: false },
      movable: {
        free: false,
        color,
        dests: this.dests,
        showDests: true,
        rookCastle: true,
        events: { after: this.handleAfter },
      },
      premovable: { enabled: false },
      selectable: { enabled: true },
      draggable: { enabled: true, showGhost: true },
      drawable: {
        enabled: true,
        visible: true,
        brushes: chessgroundBrushes(),
        autoShapes: this.autoShapes(input),
      },
    };
  }

  /** Arrows in the current board skin (src/board/theme.ts owns every colour). */
  private autoShapes(input: BoardInput): DrawShape[] {
    const theme = readBoardTheme();
    return (input.arrows ?? []).flatMap((arrow) => {
      const squares = uciToKeyPair(arrow.uci);
      if (squares === null) return [];
      return [{
        orig: squares[0],
        dest: squares[1],
        brush: brushName(theme, arrow.brush),
        label: arrow.label ? { text: arrow.label } : undefined,
      }];
    });
  }
}
